import { Component, Inject, OnDestroy, ChangeDetectorRef } from '@angular/core';

import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Subscription } from 'rxjs';
import { BackupService, RestoreProgressEvent } from '../../../../services/backup.service';

export interface RestoreProgressDialogData {
  backupId: string;
}

interface PhaseInfo {
  icon: string;
  label: string;
}

const PHASE_MAP: Record<string, PhaseInfo> = {
  init: { icon: 'hourglass_empty', label: 'Előkészítés' },
  clear: { icon: 'cleaning_services', label: 'Adattáblák tisztítása' },
  parse: { icon: 'description', label: 'Backup fájl feldolgozása' },
  restore: { icon: 'cloud_download', label: 'Adatok visszaállítása' },
  verify: { icon: 'verified', label: 'Ellenőrzés' },
  done: { icon: 'check_circle', label: 'Kész' },
  error: { icon: 'error', label: 'Hiba' },
};

@Component({
  selector: 'app-restore-progress-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule
],
  template: `
    <div class="restore-progress-dialog crm-dialog">
      <h2 mat-dialog-title>
        <mat-icon>restore</mat-icon>
        Adatbázis visszaállítás
      </h2>

      <mat-dialog-content>
        <!-- Progress bar -->
        <div class="progress-section">
          <mat-progress-bar
            [mode]="isComplete || isError ? 'determinate' : 'buffer'"
            [value]="progress"
            [bufferValue]="progress + 10"
            [color]="isError ? 'warn' : 'primary'">
          </mat-progress-bar>
          <div class="progress-percentage">{{ progress }}%</div>
        </div>

        <!-- Fázisok listája -->
        <div class="phases-list">
          @for (phase of phases; track phase.key) {
            <div class="phase-item" [class.active]="currentPhase === phase.key"
                 [class.completed]="isPhaseCompleted(phase.key)"
                 [class.error]="isError && currentPhase === phase.key">
              <mat-icon class="phase-icon">
                @if (isPhaseCompleted(phase.key)) {
                  check_circle
                } @else if (isError && currentPhase === phase.key) {
                  error
                } @else if (currentPhase === phase.key) {
                  sync
                } @else {
                  radio_button_unchecked
                }
              </mat-icon>
              <span class="phase-label">{{ phase.label }}</span>
              @if (currentPhase === phase.key && currentMessage) {
                <span class="phase-message">{{ currentMessage }}</span>
              }
            </div>
          }
        </div>

        <!-- Hiba üzenet -->
        @if (isError) {
          <div class="error-message">
            <mat-icon>warning</mat-icon>
            <span>{{ errorMessage }}</span>
          </div>
        }

        <!-- Sikeres befejezés -->
        @if (isComplete) {
          <div class="success-message">
            <mat-icon>check_circle</mat-icon>
            <span>Az adatbázis sikeresen visszaállítva! Az oldal hamarosan újratöltődik.</span>
          </div>
        }
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        @if (isError) {
          <button mat-button (click)="close()">Bezárás</button>
        }
        @if (isComplete) {
          <button mat-raised-button color="primary" (click)="close()">Rendben</button>
        }
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .restore-progress-dialog {
      min-width: 420px;

      h2 {
        display: flex;
        align-items: center;
        gap: 8px;
        mat-icon { color: #1976d2; }
      }
    }

    .progress-section {
      margin: 16px 0;
      display: flex;
      align-items: center;
      gap: 12px;

      mat-progress-bar { flex: 1; }

      .progress-percentage {
        font-weight: 600;
        font-size: 14px;
        min-width: 40px;
        text-align: right;
        color: #1976d2;
      }
    }

    .phases-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin: 16px 0;
    }

    .phase-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 8px;
      border-radius: 6px;
      transition: all 0.3s ease;
      color: #999;

      &.active {
        background: #e3f2fd;
        color: #1976d2;
        font-weight: 500;

        .phase-icon { animation: spin 1.5s linear infinite; }
      }

      &.completed {
        color: #4caf50;
      }

      &.error {
        background: #fce4ec;
        color: #f44336;

        .phase-icon { animation: none; }
      }

      .phase-label {
        flex: 1;
        font-size: 13px;
      }

      .phase-message {
        font-size: 11px;
        color: #666;
        max-width: 200px;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .error-message, .success-message {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px;
      border-radius: 6px;
      margin-top: 12px;
      font-size: 13px;
    }

    .error-message {
      background: #fce4ec;
      color: #c62828;
    }

    .success-message {
      background: #e8f5e9;
      color: #2e7d32;
    }
  `]
})
export class RestoreProgressDialogComponent implements OnDestroy {
  progress = 0;
  currentPhase = 'init';
  currentMessage = '';
  isComplete = false;
  isError = false;
  errorMessage = '';
  private subscription?: Subscription;

  phases = [
    { key: 'init', label: 'Előkészítés' },
    { key: 'clear', label: 'Adattáblák tisztítása' },
    { key: 'parse', label: 'Backup fájl feldolgozása' },
    { key: 'restore', label: 'Adatok visszaállítása' },
    { key: 'verify', label: 'Ellenőrzés' },
  ];

  private phaseOrder = ['init', 'clear', 'parse', 'restore', 'verify', 'done'];

  constructor(
    private dialogRef: MatDialogRef<RestoreProgressDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RestoreProgressDialogData,
    private backupService: BackupService,
    private cdr: ChangeDetectorRef
  ) {
    this.dialogRef.disableClose = true;
    this.startRestore();
  }

  private startRestore(): void {
    this.subscription = this.backupService.restoreBackupStream(this.data.backupId).subscribe({
      next: (event: RestoreProgressEvent) => {
        this.progress = event.progress;
        this.currentPhase = event.phase;
        this.currentMessage = event.message;

        if (event.success) {
          this.isComplete = true;
          this.currentPhase = 'done';
          this.dialogRef.disableClose = false;
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isError = true;
        this.errorMessage = err.message || 'Ismeretlen hiba történt';
        this.dialogRef.disableClose = false;
        this.cdr.markForCheck();
      }
    });
  }

  isPhaseCompleted(phaseKey: string): boolean {
    if (this.isComplete) return true;
    const currentIdx = this.phaseOrder.indexOf(this.currentPhase);
    const phaseIdx = this.phaseOrder.indexOf(phaseKey);
    return phaseIdx < currentIdx;
  }

  close(): void {
    this.dialogRef.close({ success: this.isComplete });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
